import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SoecoButton } from "../../components/SoecoButton";
import { EmployeeContext } from "../../context/EmployeeContext";
import { isGlobalAdmin, isValidEmail } from "../../utils/util";
import { AdminCompanyFilter } from "../../components/AdminCompanyFilter";
import { useGetCompany, useGetSubCompanies } from "../../hooks/companyHooks";
import {
    EditEmployee,
    useDeleteEmployee,
    useUpdateEmployee,
    useGetEmployee,
} from "../../hooks/employeeHooks";
import { ErrorRecord } from "../../types";
import DeleteIcon from "@mui/icons-material/Delete";
import styled from "styled-components";
import { SoecoHeader } from "../../components/SoecoHeader";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../../components/Modals/Modal";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { useSnackBar } from "../../context/SnackbarContext";
import { ChevronDown, UserRoundPen, Trash2 } from "lucide-react";
import { sx } from "../../utils/util";

type EditEmployeeForm = {
    name: string;
    email: string;
    role: string;
    companyId?: string;
    lastName?: string;
    phone?: string;
    language?: string;
};

export const EditUser = () => {
    const { companyId, employeeId } = useParams();
    const { data: company } = useGetCompany(companyId ?? "");
    const { data: companies } = useGetSubCompanies(companyId ?? "");

    const { data: employeeData } = useGetEmployee(companyId ?? "", employeeId ?? "");
    const { employee } = useContext(EmployeeContext);
    const updateMutation = useUpdateEmployee();
    const deleteMutation = useDeleteEmployee();
    const navigate = useNavigate();
    const [form, setForm] = useState<EditEmployeeForm>({
        name: "",
        email: "",
        role: "",
        lastName: "",
        phone: "",
        language: "",
    });

    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<EditEmployeeForm>>({});
    const [disableButton, setDisableButton] = useState<boolean>(false);

    useEffect(() => {
        if (employeeData) {
            setForm({
                name: employeeData.name ?? "",
                email: employeeData.email ?? "",
                role: employeeData.role ?? "",
                lastName: employeeData.lastName ?? "",
                phone: employeeData.phone ?? "",
                language: employeeData.language ?? "",
            });
        }
    }, [employeeData]);

    const handleChangeSelect = (e: SelectChangeEvent<string>) => {
        setForm({ ...form, role: e.target.value });
        setErrors({});
        setError(false);
    };

    const handleLanguageChange = (e: SelectChangeEvent<string>) => {
        setForm({ ...form, language: e.target.value });
    };

    const transformFormData = (form: EditEmployeeForm): EditEmployee => ({
        name: form.name,
        email: form.email.toLowerCase(),
        role: form.role,
        lastName: form.lastName,
        phone: form.phone,
        language: form.language,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setDisableButton(false);
        setError(false);
    };

    const isValidForm = () => {
        let valid = true;
        const newErrors: ErrorRecord<EditEmployeeForm> = {};
        if (!isValidEmail(form.email)) {
            valid = false;
            newErrors.email = "Please enter a valid email address";
        }
        if (form.name.length < 1) {
            valid = false;
            newErrors.name = "name cannot be empty";
        }
        if (!valid) {
            setErrors(newErrors);
            setError(true);
            setDisableButton(true);
            return false;
        } else {
            setErrors({});
            setError(false);
            setDisableButton(false);
            return true;
        }
    };

    const snackBar = useSnackBar();

    const handleSubmit = () => {
        if (isValidForm()) {
            if (!employeeId) {
                return;
            }

            const targetCompanyId = form.companyId ?? companyId ?? employee.companyId;

            updateMutation.mutate(
                {
                    companyId: targetCompanyId,
                    employeeId: employeeId,
                    employee: { ...transformFormData(form), companyId: targetCompanyId },
                },
                {
                    onError: error => {
                        if (error instanceof Error) {
                            snackBar.showSnackBar(error.message, "error");
                            setDisableButton(true);
                        }
                    },
                    onSuccess: () => {
                        snackBar.showSnackBar("Employee updated successfully", "success");
                        navigate(-1);
                    },
                },
            );
        }
    };

    const handleDeleteEmployee = () => {
        if (!companyId || !employeeId) {
            return;
        }
        deleteMutation.mutate(
            { companyId, employeeId },
            {
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                        setDisableButton(true);
                    }
                },
                onSuccess: () => {
                    snackBar.showSnackBar("Employee deleted", "success");
                    navigate(-1);
                },
            },
        );
    };

    const { isShown, toggle } = useModal();

    const onConfirm = () => {
        toggle();
        handleDeleteEmployee();
    };
    const onCancel = () => toggle();

    const onChange = (id: string) => {
        if (employeeData) {
            setForm({
                name: employeeData.name ?? "",
                email: employeeData.email ?? "",
                role: employeeData.role ?? "",
                companyId: id,
                lastName: employeeData.lastName ?? "",
                phone: employeeData.phone ?? "",
                language: employeeData.language ?? "",
            });
        }
    };

    return (
        <EditUserPageWrapper>
            <EditUserWrapper>
                <Modal
                    isShown={isShown}
                    hide={toggle}
                    headerText="Delete the user?"
                    modalContent={
                        <ErrorModal
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            message="You will not be able to recover it"
                        />
                    }
                />
                <SoecoHeader>Edit User</SoecoHeader>
                <Form>
                    <div style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
                        <UserRoundPen size={96} color="#cbcbcb" />
                    </div>
                    {isGlobalAdmin(employee) && companyId && (
                        <AdminCompanyFilter
                            currentCompanyId={companyId}
                            onChange={onChange}
                            width="100%"
                        />
                    )}

                    {!isGlobalAdmin(employee) && company && (
                        <TextField
                            label="Company"
                            contentEditable={false}
                            value={company?.name}
                            variant="outlined"
                            color="secondary"
                            sx={{
                                "& .MuiInputLabel-root": menuItemSx,
                            }}
                        />
                    )}

                    {employeeData && employee.role === "superAdmin" ? ( //isGlobalAdmin(employee) ? (
                        <FormControl>
                            <InputLabel
                                sx={{
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                    fontSize: "16px",
                                    fontWeight: "400",
                                    color: "rgba(0, 0, 0, 0.50)",
                                }}
                            >
                                Role
                            </InputLabel>
                            <Select
                                label="Role"
                                value={form.role}
                                onChange={handleChangeSelect}
                                IconComponent={ChevronDown}
                                defaultValue="Role"
                                sx={menuItemSx}
                            >
                                <MenuItem value={"user"} sx={menuItemSx}>
                                    User
                                </MenuItem>
                                <MenuItem value={"admin"} sx={menuItemSx}>
                                    Admin
                                </MenuItem>
                                <MenuItem value={"master"} sx={menuItemSx}>
                                    Master
                                </MenuItem>
                            </Select>
                        </FormControl>
                    ) : (
                        employeeData && (
                            <Select label="Role" value={form.role} onChange={handleChangeSelect}>
                                <MenuItem value={"user"} sx={sx}>
                                    User
                                </MenuItem>
                                <MenuItem value={"admin"} sx={sx}>
                                    Admin
                                </MenuItem>
                            </Select>
                        )
                    )}
                    <TextField
                        name="email"
                        autoComplete="off"
                        error={error && errors.email ? true : false}
                        value={form.email}
                        label="E-mail"
                        variant="outlined"
                        helperText={error && errors.email}
                        onChange={handleChange}
                        sx={sx}
                    />
                    <TextField
                        name="name"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.name}
                        label="First name"
                        variant="outlined"
                        value={form.name}
                        onChange={handleChange}
                        sx={sx}
                    />
                    <TextField
                        name="lastName"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.lastName}
                        label="Last name"
                        variant="outlined"
                        value={form.lastName}
                        onChange={handleChange}
                        sx={sx}
                    />
                    <TextField
                        name="phone"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.phone}
                        label="Phone number"
                        variant="outlined"
                        value={form.phone}
                        onChange={handleChange}
                        sx={sx}
                    />
                    <FormControl>
                        <InputLabel
                            sx={{
                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                fontSize: "16px",
                                fontWeight: "400",
                                color: "rgba(0, 0, 0, 0.50)",
                            }}
                        >
                            Language
                        </InputLabel>
                        <Select
                            label="Language"
                            value={form.language}
                            onChange={handleLanguageChange}
                            IconComponent={ChevronDown}
                            defaultValue="en"
                            sx={menuItemSx}
                        >
                            <MenuItem value={"en"} sx={menuItemSx}>
                                English
                            </MenuItem>
                            <MenuItem value={"sv"} sx={menuItemSx}>
                                Swedish
                            </MenuItem>
                            <MenuItem value={"dk"} sx={menuItemSx}>
                                Danish
                            </MenuItem>
                            <MenuItem value={"no"} sx={menuItemSx}>
                                Norwegian
                            </MenuItem>
                            <MenuItem value={"fi"} sx={menuItemSx}>
                                Finnish
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Form>
            </EditUserWrapper>
            <ActionButtons>
                <SoecoButton fullWidth size="large" color="warning" onClick={() => navigate(-1)}>
                    Back
                </SoecoButton>
                <SoecoButton
                    fullWidth
                    size="large"
                    disabled={updateMutation.isPending || disableButton}
                    color="primary"
                    onClick={handleSubmit}
                >
                    Update
                </SoecoButton>{" "}
                <SoecoButton
                    color="primary"
                    onClick={toggle}
                    disabled={employee.role !== "superAdmin"}
                >
                    <DeleteIcon fontSize="large" color="warning" />
                </SoecoButton>
            </ActionButtons>
        </EditUserPageWrapper>
    );
};

const EditUserPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 80vh;
    align-items: center;
    gap: 2rem;
`;

const EditUserWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 70%;
    align-items: center;
    gap: 2rem;
`;

const Form = styled(FormControl)`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    margin: 0 auto;
    gap: 20px;
`;

const ActionButtons = styled.div`
    display: flex;
    width: 90%;
    gap: 2rem;
    position: fixed;
    bottom: 2rem;
`;

const menuItemSx = {
    fontFamily: "'Yllw Sans a1 241219', sans-serif",
    fontSize: "16px",
    fontWeight: "400",
    color: "black",
    opacity: 1,
};
